import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { cn } from '@plato/ui/util/classes';
import { cva, type VariantProps } from 'class-variance-authority';
import { InfoIcon, XIcon, type LucideProps } from 'lucide-react';
import * as React from 'react';
import { AiIcon } from '../../assets/icons/ai-icon.tsx';
import Text from './text.tsx';

export type NoticeVariant = 'default' | 'warning' | 'info' | 'ai';

const noticeVariants = cva('flex flex-col sm:flex-row rounded-md border px-3 py-1.5 text-sm font-normal', {
    variants: {
        variant: {
            default: 'bg-neutral-50 text-neutral-500 border-neutral-100 ',
            warning: 'bg-red-50 text-red-600 border-red-300 ',
            info: 'bg-blue-50 text-blue-400 border-blue-300 ',
            ai: 'border-0 bg-linear-to-r from-[#4c74e61a] to-[#8d54e91a] text-neutral-700',
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});

const noticeDismissVariants = cva(
    'flex min-h-5 shrink-0 items-center rounded transition-colors cursor-pointer',
    {
        variants: {
            variant: {
                default: 'text-neutral-400 hover:text-neutral-700',
                warning: 'text-red-400 hover:text-red-800',
                info: 'text-blue-400 hover:text-blue-700',
                ai: 'text-neutral-400 hover:text-neutral-800',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

type NoticeDismissProps =
    | { onDismiss: () => void; dismissLabel: string }
    | { onDismiss?: never; dismissLabel?: never };

export type NoticeProps = {
    /** Intent variant */
    variant: NoticeVariant;

    /** Optional heading text */
    title?: React.ReactNode;

    /** Description text or content */
    description?: React.ReactNode;

    /** Additional CSS class(es) for styling */
    className?: string;

    /** Pass `null` to render no icon at all */
    icon?: React.ComponentType<LucideProps> | null;

    withTooltip?: boolean;

    action?: React.ReactNode;
} & NoticeDismissProps &
    Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
    VariantProps<typeof noticeVariants>;

export const Notice = React.forwardRef<HTMLDivElement, NoticeProps>(
    (
        {
            variant,
            title,
            description,
            className,
            icon: IconComponent = InfoIcon,
            withTooltip,
            action,
            onDismiss,
            dismissLabel,
            ...props
        },
        ref,
    ) => {
        const icon = variant === 'ai' ? <AiIcon /> : IconComponent && <IconComponent size={16} />;

        return (
            <div ref={ref} className={cn(noticeVariants({ variant }), className)} {...props}>
                <div className="flex items-start gap-1 grow">
                    {icon && <div className="flex items-center min-h-5">{icon}</div>}
                    <div
                        className={cn('grow', {
                            'flex items-center gap-1': withTooltip || (title && description),
                        })}
                    >
                        {title && (
                            <Text weight="semibold" color="neutral-800">
                                {title}
                            </Text>
                        )}
                        {!withTooltip && description && <Text color="neutral-700">{description}</Text>}
                        {withTooltip && (
                            <InfoHint>
                                <InfoHintTrigger />
                                <InfoHintContent>
                                    <InfoHintHeader>
                                        <InfoHintTitle>{title}</InfoHintTitle>
                                    </InfoHintHeader>
                                    <InfoHintBody>{description}</InfoHintBody>
                                </InfoHintContent>
                            </InfoHint>
                        )}
                    </div>
                    {onDismiss && (
                        <button
                            type="button"
                            aria-label={dismissLabel}
                            onClick={onDismiss}
                            className={noticeDismissVariants({ variant })}
                        >
                            <XIcon size={16} />
                        </button>
                    )}
                </div>
                {action && <div className="flex">{action}</div>}
            </div>
        );
    },
);

Notice.displayName = 'Notice';
